import ReactMarkdown from 'react-markdown';
import { visit } from 'unist-util-visit';
import type { Components } from 'react-markdown';
import type { Plugin } from 'unified';
import type { Parent, Node } from 'unist';
import { ProjectStatusChip, TicketStatusChip, KrStatusChip, RoleChip } from './StatusChip';
import type { ProjectStatus, TicketStatus, KrStatus, FunctionalRole } from '../types';

interface Text extends Node {
  type: 'text';
  value: string;
}

interface MentionNode extends Node {
  type: 'mention';
  value: string;
}

interface StatusNode extends Node {
  type: 'statusChip';
  kind: 'project' | 'ticket' | 'kr' | 'role';
  value: string;
}

// Status codes that are clearly programmatic (underscore form or known keywords)
const PROJECT_STATUS_TOKENS: ProjectStatus[] = ['at_risk', 'on_track', 'planning', 'draft', 'complete', 'archived', 'active'];
const TICKET_STATUS_TOKENS: TicketStatus[] = ['triage', 'at_risk', 'on_track', 'planning', 'complete', 'idea', 'invalid'];
const KR_STATUS_TOKENS: KrStatus[] = ['not_started', 'in_progress', 'at_risk', 'achieved'];
const ROLE_TOKENS: FunctionalRole[] = ['product_manager', 'team_lead', 'engineer', 'designer', 'executive', 'sales', 'marketing'];

// Underscore-form tokens + single-word tokens that are distinctive enough in PM context
const SINGLE_WORD_SAFE: string[] = ['triage', 'idea'];
const STATUS_PATTERN = new RegExp(
  `\\b(${[
    ...new Set([...PROJECT_STATUS_TOKENS, ...TICKET_STATUS_TOKENS, ...KR_STATUS_TOKENS, ...ROLE_TOKENS]),
  ]
    .filter(s => s.includes('_') || SINGLE_WORD_SAFE.includes(s))
    .join('|')})\\b`,
  'g',
);

// Pre-process raw markdown to normalise integration-specific mention syntax before remark runs.
// Jira cloud:  [~accountid:uuid] or [~accountid:prefix:uuid]  →  @uuid-prefix (first 8 chars of the id)
// Jira server: [~username]                                   →  @username
//
// Backend sync (app/utils/jira_markup.py) resolves these to real display
// names at ingest time going forward, but this defensively re-processes
// already-stored data too — including a backslash-escaped form that has
// been observed in live Firestore documents (\[\~accountid:...\]), which
// otherwise renders as literal bracket/tilde noise once react-markdown
// resolves the escapes. Optional `\` before each special char is tolerated
// on both the mention regexes AND the surrounding "[Author]: " prefix isn't
// affected since it never contains these chars.
function preprocessMarkdown(text: string): string {
  const s = typeof text === 'string' ? text : String(text ?? '');
  return s
    .replace(/\\?\[\\?~accountid:([a-z0-9:-]+)\\?\]/gi, (_, id: string) => `@${id.replace(/[^a-f0-9]/gi, '').slice(0, 8)}`)
    .replace(/\\?\[\\?~([^\]\\]+)\\?\]/g, (_, username: string) => `@${username}`)
    // Slack channel refs (<#C01ABC|general>) and special mentions
    // (<!here>/<!channel>/<!everyone>) — not yet reachable from live data
    // (Slack ticket-sync is a stub), but this component is the single
    // rendering path for provider text, so it's handled here for when that
    // sync ships rather than left as a second fix later.
    .replace(/<#[A-Z0-9]+\|([^>]+)>/g, (_, name: string) => `#${name}`)
    .replace(/<!(here|channel|everyone)>/g, (_, kind: string) => `@${kind}`)
    // Slack link syntax (<https://url|label>) → standard markdown link so
    // it renders through the `a` component instead of as literal angle
    // brackets. Bare <https://url> needs no handling — GFM autolinks it.
    .replace(/<(https?:\/\/[^|>]+)\|([^>]+)>/g, (_, url: string, label: string) => `[${label}](${url})`);
}

function classifyStatus(value: string): { kind: StatusNode['kind'] } | null {
  if ((PROJECT_STATUS_TOKENS as string[]).includes(value)) return { kind: 'project' };
  if ((TICKET_STATUS_TOKENS as string[]).includes(value)) return { kind: 'ticket' };
  if ((KR_STATUS_TOKENS as string[]).includes(value)) return { kind: 'kr' };
  if ((ROLE_TOKENS as string[]).includes(value)) return { kind: 'role' };
  return null;
}

const remarkStatusChips: Plugin = () => (tree) => {
  visit(tree, 'text', (node: Text, index: number | undefined, parent: Parent | undefined) => {
    if (index === undefined || !parent) return;
    const text = node.value;
    STATUS_PATTERN.lastIndex = 0;
    if (!STATUS_PATTERN.test(text)) return;

    STATUS_PATTERN.lastIndex = 0;
    const newNodes: (Text | StatusNode)[] = [];
    let last = 0;
    let match: RegExpExecArray | null;

    while ((match = STATUS_PATTERN.exec(text)) !== null) {
      const classification = classifyStatus(match[0]);
      if (!classification) continue;
      if (match.index > last) {
        newNodes.push({ type: 'text', value: text.slice(last, match.index) } as Text);
      }
      newNodes.push({ type: 'statusChip', kind: classification.kind, value: match[0] } as StatusNode);
      last = match.index + match[0].length;
    }
    if (last < text.length) {
      newNodes.push({ type: 'text', value: text.slice(last) } as Text);
    }
    if (newNodes.length > 1) {
      parent.children.splice(index, 1, ...newNodes);
    }
  });
};

// Remark plugin: converts @mention and <@SLACK_ID> inline text to mention nodes
const remarkMentions: Plugin = () => (tree) => {
  visit(tree, 'text', (node: Text, index: number | undefined, parent: Parent | undefined) => {
    if (index === undefined || !parent) return;

    // Match @word (Linear/general), <@SLACKID> (Slack), and Jira normalised short IDs
    const pattern = /(<@[A-Z0-9]+>|@[\w][\w.-]*)/g;
    const text = node.value;
    if (!pattern.test(text)) return;

    pattern.lastIndex = 0;
    const newNodes: (Text | MentionNode)[] = [];
    let last = 0;
    let match: RegExpExecArray | null;

    while ((match = pattern.exec(text)) !== null) {
      if (match.index > last) {
        newNodes.push({ type: 'text', value: text.slice(last, match.index) } as Text);
      }
      // Strip Slack angle brackets if present
      const raw = match[0];
      const name = raw.startsWith('<@') ? raw.slice(2, -1) : raw.slice(1);
      newNodes.push({ type: 'mention', value: name } as MentionNode);
      last = match.index + raw.length;
    }
    if (last < text.length) {
      newNodes.push({ type: 'text', value: text.slice(last) } as Text);
    }

    if (newNodes.length > 1) {
      parent.children.splice(index, 1, ...newNodes);
    }
  });
};

interface MarkdownProps {
  children: string;
  className?: string;
  /** 'sm' (default) | 'xs' — controls base text size */
  size?: 'xs' | 'sm';
}

export function Markdown({ children, className = '', size = 'sm' }: MarkdownProps) {
  // Two internal scales, both descending from heading to body — the heading
  // steps must always sit ABOVE the paragraph size or hierarchy inverts.
  // 'sm' (default): body 16 / h3 16 / h2 18 / h1 22.
  // 'xs' (compact, e.g. AI summaries in cards): body 14 / h3 14 / h2 14+600 / h1 16.
  const base = size === 'xs' ? 'text-meta' : 'text-body';
  const h1Size = size === 'xs' ? 'text-body' : 'text-title';
  const h2Size = size === 'xs' ? 'text-meta' : 'text-lg';

  const components: Components = {
    // @ts-expect-error — custom node type registered by remarkStatusChips
    statusChip: ({ node }: { node: StatusNode }) => {
      const v = node.value;
      if (node.kind === 'project') return <span className="mx-0.5 align-middle inline-flex"><ProjectStatusChip status={v as ProjectStatus} /></span>;
      if (node.kind === 'ticket')  return <span className="mx-0.5 align-middle inline-flex"><TicketStatusChip status={v as TicketStatus} /></span>;
      if (node.kind === 'kr')      return <span className="mx-0.5 align-middle inline-flex"><KrStatusChip status={v as KrStatus} /></span>;
      if (node.kind === 'role')    return <span className="mx-0.5 align-middle inline-flex"><RoleChip role={v as FunctionalRole} /></span>;
      return <>{v}</>;
    },
    mention: ({ node }: { node: MentionNode }) => (
      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-sky-500/10 border border-sky-500/30 text-sky-400 text-micro font-medium whitespace-nowrap mx-0.5">
        @{node.value}
      </span>
    ),
    h1: ({ children }) => (
      <h1 className={`font-semibold text-gray-900 dark:text-white ${h1Size} mt-3 mb-1 first:mt-0`}>{children}</h1>
    ),
    h2: ({ children }) => (
      <h2 className={`font-semibold text-gray-900 dark:text-white ${h2Size} mt-3 mb-1 first:mt-0`}>{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 className={`font-medium text-gray-800 dark:text-gray-100 ${base} mt-2 mb-0.5 first:mt-0`}>{children}</h3>
    ),
    p: ({ children }) => (
      <p className={`${base} leading-relaxed`}>{children}</p>
    ),
    ul: ({ children }) => (
      <ul className="space-y-0.5 my-1 pl-1">{children}</ul>
    ),
    ol: ({ children }) => (
      <ol className="space-y-0.5 my-1 pl-1 list-decimal list-inside">{children}</ol>
    ),
    li: ({ children }) => (
      <li className={`${base} leading-relaxed flex gap-1.5`}>
        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-gray-400 dark:bg-gray-500 flex-shrink-0" />
        <span>{children}</span>
      </li>
    ),
    strong: ({ children }) => (
      <strong className="font-semibold text-gray-900 dark:text-white">{children}</strong>
    ),
    em: ({ children }) => (
      <em className="italic text-gray-600 dark:text-gray-400">{children}</em>
    ),
    code: ({ children }) => (
      <code className={`${base} font-mono bg-gray-100 dark:bg-white/[0.08] text-gray-800 dark:text-gray-200 px-1 py-0.5 rounded`}>{children}</code>
    ),
    blockquote: ({ children }) => (
      <blockquote className="border-l-2 border-primary/40 pl-3 italic text-gray-500 dark:text-gray-400 my-2">{children}</blockquote>
    ),
    hr: () => <hr className="border-gray-200 dark:border-white/[0.08] my-3" />,
    a: ({ href, children }) => (
      <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{children}</a>
    ),
  };

  return (
    <div className={`space-y-1.5 ${className}`}>
      <ReactMarkdown remarkPlugins={[remarkMentions, remarkStatusChips]} components={components}>{preprocessMarkdown(children)}</ReactMarkdown>
    </div>
  );
}
